import { ERROR_CODES } from "@gatiq/shared";
import { ApiClientError, api } from "./api";

/**
 * The karigar's side of the rewards ledger.
 *
 * Balance, history and scanning all read the same endpoint, so the types and
 * the bilingual wording live here rather than being restated on each screen.
 * Every string is Hindi first, English second (D-11) — these screens are used
 * by people who may read only one of the two.
 */
export type LedgerKind = "earn" | "redeem" | "adjust" | "reverse";

export interface LedgerRow {
  id: string;
  kind: LedgerKind;
  /** Signed: positive credits the karigar, negative debits them. */
  points: number;
  reason: string | null;
  /** The product the scanned code was printed for, when the batch had one. */
  product?: string | null;
  at: string;
}

export interface PointsPage {
  balance: number;
  items: LedgerRow[];
  total: number;
  page: number;
  totalPages: number;
}

export interface ScanResult {
  points: number;
  balance: number;
}

export const LEDGER_LABEL: Record<LedgerKind, { hi: string; en: string }> = {
  earn: { hi: "कोड स्कैन किया", en: "Code scanned" },
  redeem: { hi: "QCoin रिडीम किए", en: "QCoin redeemed" },
  adjust: { hi: "सुधार", en: "Correction" },
  reverse: { hi: "वापस लिया गया", en: "Reversed" },
};

export function fetchPoints(page = 1, pageSize = 20): Promise<PointsPage> {
  return api.get<PointsPage>(`/org/me/points?page=${page}&pageSize=${pageSize}`);
}

export function submitScan(code: string): Promise<ScanResult> {
  return api.post<ScanResult>("/org/me/scan", { code });
}

/**
 * What a failed scan should say, in both languages.
 *
 * The server's messages are English-only, so a bilingual screen cannot simply
 * print them. Mapping by error code instead keeps the wording in the app's own
 * voice, and keeps the four "no" cases distinct: a code that was never issued,
 * one this karigar already used, one somebody else used, and one the brand
 * withdrew. Each needs a different next action.
 */
export function scanErrorText(err: unknown): { hi: string; en: string } {
  const code = err instanceof ApiClientError ? err.code : null;

  switch (code) {
    case ERROR_CODES.CODE_USED_BY_YOU:
      return {
        hi: "यह कोड आप पहले ही स्कैन कर चुके हैं। अंक आपके खाते में हैं।",
        en: "You have already scanned this code. The points are in your account.",
      };
    case ERROR_CODES.CODE_ALREADY_USED:
      return {
        hi: "यह कोड किसी और ने पहले स्कैन कर लिया है। दुकानदार को दिखाएँ।",
        en: "Someone else has already scanned this code. Show it to your shopkeeper.",
      };
    case ERROR_CODES.CODE_NOT_AT_SHOP:
      return {
        hi: "यह माल अभी दुकान तक नहीं पहुँचा है। दुकानदार से माल रिसीव करवाएँ, फिर दोबारा स्कैन करें।",
        en: "This product hasn't reached a shop yet. Ask the shop to receive it, then scan again.",
      };
    case ERROR_CODES.CODE_VOID:
      return {
        hi: "यह कोड कंपनी ने वापस ले लिया है। इसके अंक नहीं मिलेंगे।",
        en: "The brand has withdrawn this code. It cannot be used.",
      };
    case ERROR_CODES.NOT_FOUND:
      return {
        hi: "यह कोड पहचाना नहीं गया। दोबारा देखें कि पूरा कोड सही है।",
        en: "This code was not recognised. Check that you have the whole code.",
      };
    case ERROR_CODES.VALIDATION_FAILED:
      return {
        hi: "कोड पूरा नहीं है। दोबारा स्कैन करें या पूरा कोड लिखें।",
        en: "That code is incomplete. Scan again, or type the full code.",
      };
    default:
      return fallbackText(err);
  }
}

const OFFLINE = {
  hi: "अभी जुड़ नहीं पा रहे। इंटरनेट देखें और दोबारा कोशिश करें।",
  en: "Could not reach the server. Check your internet and try again.",
};

/** The server answered but with something unexpected, or it could not be reached at all — never raw English. */
function fallbackText(err: unknown): { hi: string; en: string } {
  if (!(err instanceof ApiClientError)) return OFFLINE;
  if (err.code === ERROR_CODES.RATE_LIMITED) {
    return { hi: "बहुत ज़्यादा कोशिशें हुईं। थोड़ी देर बाद करें।", en: "Too many tries. Please wait a little and try again." };
  }
  return { hi: "अभी नहीं हो पाया। दोबारा कोशिश करें।", en: "That did not work. Please try again." };
}

/** Karigar sign-in (OTP) errors, in both languages. */
export function otpErrorText(err: unknown): { hi: string; en: string } {
  const code = err instanceof ApiClientError ? err.code : null;
  switch (code) {
    case ERROR_CODES.OTP_INVALID:
      return { hi: "कोड गलत है। दोबारा देखें।", en: "That code is not right. Check it and try again." };
    case ERROR_CODES.OTP_EXPIRED:
      return { hi: "कोड की समय-सीमा खत्म हो गई। नया कोड मंगाएँ।", en: "That code has expired. Ask for a new one." };
    case ERROR_CODES.OTP_ATTEMPTS_EXCEEDED:
      return { hi: "बहुत बार गलत कोड डाला। नया कोड मंगाएँ।", en: "Too many wrong codes. Ask for a new one." };
    case ERROR_CODES.OTP_COOLDOWN:
      return { hi: "नया कोड थोड़ी देर में मंगा सकते हैं।", en: "You can ask for a new code in a little while." };
    case ERROR_CODES.OTP_CHANNEL_UNAVAILABLE:
      return { hi: "अभी WhatsApp पर कोड नहीं भेज पा रहे। थोड़ी देर बाद करें।", en: "We can't send codes on WhatsApp right now. Please try again shortly." };
    case ERROR_CODES.NOT_FOUND:
      return { hi: "यह नंबर रजिस्टर नहीं है। दुकानदार के QR से रजिस्टर करें।", en: "This number is not registered. Register from your shop's QR code." };
    case ERROR_CODES.KYC_NOT_APPROVED:
      return { hi: "आपका रजिस्ट्रेशन मंज़ूर नहीं हुआ। अपने दुकानदार से बात करें।", en: "Your registration was not approved. Please speak to your shop." };
    case ERROR_CODES.ACCOUNT_SUSPENDED:
    case ERROR_CODES.ACCOUNT_PENDING:
      return { hi: "यह खाता अभी चालू नहीं है। अपने दुकानदार से बात करें।", en: "This account is not active. Please speak to your shop." };
    case ERROR_CODES.VALIDATION_FAILED:
      return { hi: "10 अंकों का सही मोबाइल नंबर डालें।", en: "Enter a valid 10-digit mobile number." };
    default:
      return fallbackText(err);
  }
}

/**
 * Pulls the reward code out of whatever the camera actually read.
 *
 * The printed QR is meant to carry the bare code, but printers are given a CSV
 * and some will wrap it in a URL so a plain camera app shows something
 * tappable. Accepting both costs a few lines and avoids a scan that looks
 * successful yet posts a string the server cannot match. The server normalises
 * case and punctuation itself, so this only has to find the right substring.
 */
export function extractCode(raw: string): string {
  const text = raw.trim();
  if (!/^https?:\/\//i.test(text)) return text;

  try {
    const url = new URL(text);
    const fromQuery = url.searchParams.get("c") ?? url.searchParams.get("code");
    if (fromQuery) return fromQuery;
    const segments = url.pathname.split("/").filter(Boolean);
    return segments[segments.length - 1] ?? text;
  } catch {
    return text;
  }
}

/* ── withdrawing ──────────────────────────────────────────────────── */

export type RedemptionStatus = "pending" | "settled" | "cancelled" | "expired";

export interface Redemption {
  id: string;
  displayId: string | null;
  points: number;
  status: RedemptionStatus;
  /** Present while pending — a spent code must not stay on screen. */
  otp: string | null;
  createdAt: string;
}

export interface RedemptionPage {
  items: Redemption[];
  total: number;
  page: number;
  totalPages: number;
}

export const REDEMPTION_LABEL: Record<RedemptionStatus, { hi: string; en: string }> = {
  pending: { hi: "दुकानदार को कोड बताएं", en: "Show the code to your shopkeeper" },
  settled: { hi: "पैसे मिल गए", en: "Paid" },
  cancelled: { hi: "रद्द किया गया — अंक वापस", en: "Cancelled — points returned" },
  /* No longer produced: codes do not time out. Kept so older rows still read. */
  expired: { hi: "समय समाप्त — अंक वापस", en: "Expired — points returned" },
};

export function fetchRedemptions(page = 1, pageSize = 20): Promise<RedemptionPage> {
  return api.get<RedemptionPage>(`/org/me/redemptions?page=${page}&pageSize=${pageSize}`);
}

export function requestRedemption(points: number): Promise<{
  redemptionId: string;
  displayId: string;
  otp: string;
  points: number;
  balance: number;
}> {
  return api.post("/org/me/redeem", { points });
}

export function cancelRedemption(id: string): Promise<void> {
  return api.del(`/org/me/redeem/${id}`);
}

/**
 * Why a withdrawal was refused, in both languages.
 *
 * The approval case is the one that matters: a karigar who has been scanning
 * for a week and is told "no" needs to hear that the points are still theirs,
 * not that something has gone wrong.
 */
export function redeemErrorText(err: unknown): { hi: string; en: string } {
  const code = err instanceof ApiClientError ? err.code : null;

  switch (code) {
    case ERROR_CODES.KYC_PENDING_FOR_REDEEM:
      return {
        hi: "आपका रजिस्ट्रेशन अभी मंज़ूर नहीं हुआ। QCoin सुरक्षित हैं — मंज़ूरी के बाद रिडीम कर सकते हैं।",
        en: "Your registration is not approved yet. Your QCoin are safe — you can redeem them once it is.",
      };
    case ERROR_CODES.INSUFFICIENT_POINTS:
      return {
        hi: "इतने अंक नहीं हैं।",
        en: "You do not have that many points.",
      };
    default:
      return fallbackText(err);
  }
}
